<template>
  <Modal
    :title="modalTitle"
    class-name="job-commissions-modal"
    @submit="handleSave"
    @hide-modal="$emit('hide-modal')"
  >
    <div>
      <CommissionInfo v-if="job.id" />
      <CommissionFields
        v-if="formData.length"
        ref="commissionFields"
        :commissions="formData"
        :technicians="options"
        @update-commission="handleUpdate"
        @delete-commission="handleDelete"
      />
      <ItemAdder text="ADD COMMISSION" slim @click="handleAdd" />
    </div>
  </Modal>
</template>

<script>
/* eslint-disable no-unused-vars */
import { mapActions, mapState } from "vuex";

import useVuelidate from "@vuelidate/core";
import ItemAdder from "../../components/ItemAdder.vue";
import Modal from "../../components/Modal.vue";
import { EventBus } from "../../lib/event_bus.js";
import { generateUID } from "../../lib/helpers.js";
import CommissionFields from "./CommissionFields.vue";
import CommissionInfo from "./CommissionInfo.vue";

export default {
  components: {
    Modal,
    ItemAdder,
    CommissionFields,
    CommissionInfo
  },
  props: {
    commissions: {
      type: Array,
      default: () => []
    },
    jobId: {
      type: Number,
      default: null
    }
  },
  emits: ["hide-modal", "save-success"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      formData: this.formatCommissions()
    };
  },
  computed: {
    ...mapState({
      job: "job",
      techniciansForJob: "techniciansForJob",
      options: ({ techniciansForJob }) =>
        techniciansForJob.map((tech) => ({
          value: tech.technician_profile_id,
          label: `${tech.first_name} ${tech.last_name}`,
          icon: tech.archived ? "fas fa-archive" : null
        }))
    }),
    modalTitle() {
      return this.job.id
        ? `Job #${this.job.id} Commissions`
        : "Job Commissions";
    }
  },
  mounted() {
    if (this.jobId) {
      this.fetchJob({ jobId: this.jobId });
    }
    if (!this.techniciansForJob.length && this.jobId) {
      this.fetchTechniciansForJob({ jobId: this.jobId });
    }
  },
  methods: {
    ...mapActions(["fetchTechniciansForJob", "setJobCommissions", "fetchJob"]),
    async validateAllCommissions() {
      const commissionFieldsComponent = this.$refs.commissionFields;
      if (
        commissionFieldsComponent &&
        typeof commissionFieldsComponent.validateAllFields === "function"
      ) {
        return await commissionFieldsComponent.validateAllFields();
      }
      return true;
    },
    formatCommissions() {
      return this.commissions.map((commission) => {
        let formattedCommission = commission;
        if (!commission.technician) {
          const { technician_profile_id, ...rest } = commission;
          formattedCommission = {
            ...rest,
            technician: {
              technician_id: technician_profile_id
            }
          };
        }
        const result = {
          ...formattedCommission,
          id: formattedCommission.id || formattedCommission.commission_id,
          amountType: commission.percentage ? "percent" : "dollars"
        };
        if (!commission.percentage) {
          result.fixed_amount_cents =
            formattedCommission.fixed_amount_cents ?? 0;
        }
        return result;
      });
    },
    handleAdd() {
      this.formData = [
        ...this.formData,
        {
          newItem: true,
          id: generateUID(),
          technician: { technician_id: null },
          percentage: 0,
          fixed_amount_cents: 0,
          amountType: "percent"
        }
      ];
    },
    handleUpdate(updatedCommision) {
      this.formData = this.formData.map((c) =>
        c.id === updatedCommision.id ? updatedCommision : c
      );
    },
    handleDelete(commission) {
      this.formData = this.formData.filter((c) => c.id !== commission.id);
    },
    async handleSave() {
      const valid = await this.validateAllCommissions();
      if (valid) {
        const commissions = this.formData.map((commission) => {
          const {
            newItem,
            amountType,
            percentage,
            fixed_amount_cents,
            ...rest
          } = commission;
          const data = {
            ...rest
          };
          if (amountType === "percent") {
            data.percentage = percentage;
          } else {
            data.fixed_amount_cents = fixed_amount_cents;
          }
          return data;
        });
        this.setJobCommissions({
          jobId: this.job.id,
          commissions
        }).then(() => {
          window.notify.success("Job commmissions updated successfully!");
          this.$emit("hide-modal");
          this.$emit("save-success");
          EventBus.$emit("refresh-datatable");
        });
      }
    }
  }
};
</script>
